/**
 * Every word the analyst reads, in one place.
 *
 * The pipeline speaks English and thinks in field names (`financial_debt_to_ebitda`,
 * `favorable_with_conditions`). An analyst in Riesgo Corporativo does not, and should never have
 * to. Nothing user-facing may print a raw identifier: if a value reaches the screen without a
 * label here, that is the bug.
 *
 * This mirrors `ui/labels.py`. The Streamlit app is still the deployed one, so the two live side
 * by side until it is retired.
 */

export const DOC_TYPES: Record<string, string> = {
  financial_statements: "Estados contables",
  bureau_report: "Informe de bureau",
  memo: "Memo interno",
  other: "Otro documento",
};

export const DOC_TYPE_SOURCE: Record<string, string> = {
  classifier: "Detectado por el sistema",
  user: "Definido por el analista",
  extension: "Deducido del archivo",
  auto: "Detectado por el sistema",
};

export const STEP_ORDER = [
  "classify",
  "extract",
  "consolidate",
  "validate",
  "enrich",
  "analyze",
  "render",
] as const;

export const STEP_LABELS: Record<string, string> = {
  classify: "Clasificar documentos",
  extract: "Extraer datos",
  consolidate: "Consolidar perfil",
  validate: "Validar y calcular",
  enrich: "Consultar fuentes externas",
  analyze: "Analizar riesgo",
  render: "Redactar informe",
};

/** What the analyst is told while a step runs, in the present tense. */
export const STEP_RUNNING: Record<string, string> = {
  classify: "Identificando qué es cada documento…",
  extract: "Leyendo los documentos y extrayendo cifras…",
  consolidate: "Unificando las cifras en un perfil…",
  validate: "Validando y calculando indicadores…",
  enrich: "Consultando BCRA y fuentes públicas…",
  analyze: "Evaluando el riesgo crediticio…",
  render: "Redactando el informe…",
};

export const CASE_STATUS: Record<string, string> = {
  draft: "Sin analizar",
  queued: "En cola",
  running: "Analizando",
  done: "Analizado",
  failed: "Con error",
};

export const RUN_STATUS: Record<string, string> = {
  pending: "En cola",
  running: "En curso",
  done: "Completo",
  failed: "Interrumpido",
  skipped: "Omitido",
};

export const RECOMMENDATIONS: Record<string, string> = {
  favorable: "Favorable",
  favorable_with_conditions: "Favorable con condiciones",
  unfavorable: "Desfavorable",
  insufficient_information: "Información insuficiente",
};

/**
 * One line saying what each recommendation claims. The analyst decides; the draft only argues —
 * and `insufficient_information` is explicitly not a rejection.
 */
export const RECOMMENDATION_MEANING: Record<string, string> = {
  favorable: "El legajo respalda el otorgamiento en las condiciones analizadas.",
  favorable_with_conditions:
    "El legajo respalda el otorgamiento sujeto a las condiciones detalladas.",
  unfavorable: "El legajo presenta riesgos que desaconsejan el otorgamiento.",
  insufficient_information: "No alcanza para concluir: falta información en el legajo.",
};

export const CATEGORIES: Record<string, string> = {
  red_flag: "Alerta crítica",
  risk: "Riesgo",
  strength: "Fortaleza",
  observation: "Observación",
};

export const SEVERITIES: Record<string, string> = { high: "Alta", medium: "Media", low: "Baja" };

export const VALIDATION_SEVERITY: Record<string, string> = {
  error: "Error",
  warning: "Observación",
  info: "Informativo",
};

export const SOURCES: Record<string, string> = { bcra_api: "BCRA", web: "Prensa y web" };

export const EVIDENCE_KINDS: Record<string, string> = {
  document: "Documento del legajo",
  web: "Fuente web",
  api: "Consulta a API",
};

/** Word is the deliverable; the rest exist for review and for the eval harness. */
/** What a person decided — deliberately not the words the model uses for its suggestion. */
export const DECISIONS: Record<string, string> = {
  approved: "Aprobado",
  approved_with_conditions: "Aprobado con condiciones",
  rejected: "Rechazado",
  more_information: "Pedir más información",
};

export const DECISION_MEANING: Record<string, string> = {
  approved: "Se otorga en las condiciones analizadas.",
  approved_with_conditions: "Se otorga sujeto a las condiciones detalladas.",
  rejected: "No se otorga.",
  more_information: "No se decide todavía: falta información en el legajo.",
};

/** Each decision's counterpart among the four recommendations, for the agreement chip. */
export const DECISION_MATCHES: Record<string, string> = {
  approved: "favorable",
  approved_with_conditions: "favorable_with_conditions",
  rejected: "unfavorable",
  more_information: "insufficient_information",
};

export const ACTIVITY: Record<string, string> = {
  case_created: "Legajo creado",
  case_updated: "Datos del legajo corregidos",
  case_archived: "Legajo archivado",
  case_restored: "Legajo restaurado",
  case_assigned: "Asignación cambiada",
  documents_added: "Documentos agregados",
  document_removed: "Documento quitado",
  run_started: "Análisis iniciado",
  decision_recorded: "Decisión registrada",
  note_added: "Nota agregada",
  figure_verified: "Cifra verificada",
};

export const ROLES: Record<string, string> = {
  analyst: "Analista",
  referent: "Referente",
  admin: "Administrador",
};

export const REPORT_FORMATS: Record<string, string> = {
  docx: "Word",
  html: "Página web",
  md: "Texto plano",
};
export const PRIMARY_REPORT_FORMAT = "docx";

export const FIELD_LABELS: Record<string, string> = {
  cuit: "CUIT",
  legal_name: "Razón social",
  sector: "Sector",
  incorporation_date: "Fecha de constitución",
  shareholders: "Accionistas",
  related_parties: "Partes relacionadas",
  revenue: "Ventas",
  cogs: "Costo de ventas",
  gross_profit: "Resultado bruto",
  ebitda: "EBITDA",
  operating_income: "Resultado operativo",
  financial_expenses: "Gastos financieros",
  net_income: "Resultado neto",
  total_assets: "Activo total",
  current_assets: "Activo corriente",
  cash: "Caja y bancos",
  receivables: "Créditos por ventas",
  inventory: "Bienes de cambio",
  fixed_assets: "Bienes de uso",
  total_liabilities: "Pasivo total",
  current_liabilities: "Pasivo corriente",
  financial_debt_short: "Deuda financiera corriente",
  financial_debt_long: "Deuda financiera no corriente",
  trade_payables: "Deudas comerciales",
  equity: "Patrimonio neto",
  auditor: "Auditor",
  audit_opinion: "Dictamen del auditor",
  going_concern_note: "Nota de empresa en marcha",
  extraordinary_items: "Resultados extraordinarios",
  gross_margin: "Margen bruto",
  ebitda_margin: "Margen EBITDA",
  net_margin: "Margen neto",
  current_ratio: "Liquidez corriente",
  quick_ratio: "Liquidez ácida",
  debt_to_equity: "Pasivo / Patrimonio neto",
  leverage: "Apalancamiento",
  financial_debt: "Deuda financiera total",
  financial_debt_to_ebitda: "Deuda financiera / EBITDA",
  interest_coverage: "Cobertura de intereses",
  working_capital: "Capital de trabajo",
  days_sales_outstanding: "Días de cobro",
  days_inventory: "Días de inventario",
  days_payable: "Días de pago",
  cash_conversion_cycle: "Ciclo de conversión de caja",
  // Las variaciones son reales: el cierre anterior se expresa en moneda del actual. La nominal va
  // al lado porque es la cifra impresa en el balance.
  revenue_growth: "Variación de ventas (real)",
  equity_growth: "Variación de patrimonio neto (real)",
  revenue_growth_nominal: "Variación de ventas (nominal)",
  equity_growth_nominal: "Variación de patrimonio neto (nominal)",
  bcra_situation_max: "Situación BCRA (máxima)",
  bcra_situation_by_entity: "Situación BCRA por entidad",
  total_financial_system_debt: "Deuda en el sistema financiero",
  bounced_checks_count: "Cheques rechazados",
  judgments: "Juicios informados",
  bureau_score: "Score de bureau",
  bureau_date: "Fecha del informe de bureau",
  management_notes: "Notas de la gerencia",
  business_description: "Descripción del negocio",
  customer_concentration: "Concentración de clientes",
  key_risks_stated: "Riesgos declarados",
  collateral_offered: "Garantías ofrecidas",
  requested_amount: "Monto solicitado",
  requested_purpose: "Destino del crédito",
};

/** How the figures are grouped on screen, in the order a credit file is read. */
export const FIELD_SECTIONS: { name: string; fields: string[] }[] = [
  {
    name: "Resultados",
    fields: [
      "revenue",
      "cogs",
      "gross_profit",
      "ebitda",
      "operating_income",
      "financial_expenses",
      "net_income",
    ],
  },
  {
    name: "Situación patrimonial",
    fields: [
      "total_assets",
      "current_assets",
      "cash",
      "receivables",
      "inventory",
      "fixed_assets",
      "total_liabilities",
      "current_liabilities",
      "financial_debt_short",
      "financial_debt_long",
      "trade_payables",
      "equity",
    ],
  },
  {
    name: "Auditoría",
    fields: ["auditor", "audit_opinion", "going_concern_note", "extraordinary_items"],
  },
  {
    name: "Identificación",
    fields: ["cuit", "legal_name", "sector", "incorporation_date", "shareholders", "related_parties"],
  },
  {
    name: "Antecedentes crediticios",
    fields: [
      "bcra_situation_max",
      "total_financial_system_debt",
      "bounced_checks_count",
      "bureau_score",
      "bureau_date",
      "bcra_situation_by_entity",
      "judgments",
    ],
  },
  {
    name: "Cualitativo y solicitud",
    fields: [
      "requested_amount",
      "requested_purpose",
      "collateral_offered",
      "business_description",
      "customer_concentration",
      "management_notes",
      "key_risks_stated",
    ],
  },
];

/** Profitability, liquidity, leverage, growth — the order a credit analyst reads them in. */
export const RATIO_ORDER = [
  "gross_margin",
  "ebitda_margin",
  "net_margin",
  "current_ratio",
  "quick_ratio",
  "working_capital",
  "debt_to_equity",
  "leverage",
  "financial_debt",
  "financial_debt_to_ebitda",
  "interest_coverage",
  "days_sales_outstanding",
  "days_inventory",
  "days_payable",
  "cash_conversion_cycle",
  "revenue_growth",
  "revenue_growth_nominal",
  "equity_growth",
  "equity_growth_nominal",
];

export function fieldLabel(name: string): string {
  return FIELD_LABELS[name] ?? name.replace(/_/g, " ");
}

export function label(map: Record<string, string>, key: string | null | undefined, fallback = "—") {
  if (!key) return fallback;
  return map[key] ?? key;
}
